//@ts-nocheck
import {
  Box,
  Button,
  Card,
  Grid,
  Typography,
  useTheme,
  alpha,
  Chip,
  Skeleton,
} from "@mui/material";
import { useState } from "react";
import { useQuery } from "react-query";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import {
  getDashboardAnalytics,
  getSessions,
  getTopFiveAnalytics,
} from "../../../api/services/api";
import { getSessionTimeslots } from "../../../utils/sessionFields";
import { AnalyticsCardTemp } from "../../../components/AnalyticsCard/AnalyticsCard";
import BarChartCard from "../../../components/BarChartCard";
import ColorTags from "../../../components/ColorTags";
import Loader from "../../../components/Loader";
import Table from "../../../components/TableComponent";
import textCapitalization from "../../../components/textCapitalization";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import LocalHospitalRoundedIcon from "@mui/icons-material/LocalHospitalRounded";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";

/* ─── Shared Premium Styles ─── */
const premiumCard = {
  borderRadius: "16px",
  border: "1px solid rgba(226, 232, 240, 0.6)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.02)",
  overflow: "hidden",
  transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
  "&:hover": {
    boxShadow: "0 4px 20px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.03)",
    transform: "translateY(-1px)",
  },
};

const sectionHeader = {
  px: 2.5,
  py: 2,
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  borderBottom: "1px solid rgba(226, 232, 240, 0.5)",
  background: "linear-gradient(135deg, #FEFEFE 0%, #F8FAFC 100%)",
};

const sectionTitle = {
  fontSize: "14px",
  fontWeight: 700,
  color: "#1E293B",
  letterSpacing: "-0.01em",
};

const sectionIconBox = (color: string) => ({
  width: 36,
  height: 36,
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: alpha(color, 0.08),
  border: `1px solid ${alpha(color, 0.12)}`,
  color: color,
});

/* ─── Stats Card Component ─── */
const StatsCard = ({
  icon,
  label,
  value,
  color,
  subtitle,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  color: string;
  subtitle?: string;
}) => (
  <Card sx={premiumCard}>
    <Box
      sx={{
        position: "relative",
        p: 3,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.4)})`,
        },
      }}
    >
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#94A3B8",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              mb: 1,
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontSize: "32px",
              fontWeight: 800,
              color: "#0F172A",
              letterSpacing: "-0.03em",
              lineHeight: 1,
              mb: subtitle ? 0.5 : 0,
            }}
          >
            {typeof value === "number"
              ? new Intl.NumberFormat().format(value)
              : value}
          </Typography>
          {subtitle && (
            <Typography
              sx={{ fontSize: "12px", color: "#64748B", mt: 0.5 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={sectionIconBox(color)}>{icon}</Box>
      </Box>
    </Box>
  </Card>
);

/* ─── Status Badge ─── */
const StatusBadge = ({ status }: { status: string }) => {
  const config: Record<string, { bg: string; color: string }> = {
    PENDING: { bg: "#FFF7ED", color: "#EA580C" },
    PAID: { bg: "#EFF6FF", color: "#2563EB" },
    STARTED: { bg: "#FFFBEB", color: "#D97706" },
    COMPLETED: { bg: "#F0FDF4", color: "#16A34A" },
    CANCELLED: { bg: "#FEF2F2", color: "#DC2626" },
    EXPIRED: { bg: "#F1F5F9", color: "#64748B" },
  };
  const c = config[status] || { bg: "#F1F5F9", color: "#64748B" };
  return (
    <Chip
      label={textCapitalization(status)}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "11px",
        letterSpacing: "0.02em",
        borderRadius: "8px",
        height: "26px",
        backgroundColor: c.bg,
        color: c.color,
        border: `1px solid ${alpha(c.color, 0.15)}`,
      }}
    />
  );
};

/* ─── Chart Legend Dot ─── */
const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: "3px",
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
    <Typography sx={{ fontSize: "13px", color: "#475569", fontWeight: 500 }}>
      {label}
    </Typography>
  </Box>
);

/* ─── Custom Tooltip ─── */
const CustomTooltipContent = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          px: 2,
          py: 1.5,
          backgroundColor: "#FFFFFF",
          borderRadius: "10px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        }}
      >
        <Typography sx={{ fontSize: "11px", color: "#94A3B8", mb: 0.25 }}>
          {payload[0].name}
        </Typography>
        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#0F172A" }}>
          {payload[0].value?.toLocaleString()}
        </Typography>
      </Box>
    );
  }
  return null;
};

const Dashboard = () => {
  const theme = useTheme();
  const [status, setStatus] = useState(["PENDING", "PAID", "STARTED"]);
  const [status2, setStatus2] = useState(["COMPLETED"]);

  const { data: analyticalData, isLoading } = useQuery(
    "dashboard-analytics",
    getDashboardAnalytics
  );

  const { data: sessionData } = useQuery(
    [
      "sessions",
      {
        status: status,
      },
    ],
    getSessions
  );

  const topSessionData = sessionData?.data?.sessions.slice(0, 5);

  const { data: reportData } = useQuery(
    [
      "sessions",
      {
        status: status2,
      },
    ],
    getSessions
  );

  const topReportData = reportData?.data?.sessions?.slice(0, 5);

  const { data: top5Data } = useQuery("top5", getTopFiveAnalytics);

  if (isLoading) {
    return <Loader />;
  }

  const COLORS = ["#0EA5E9", "#D58E4E", "#10B981"];

  const column = [
    {
      title: "Session ID",
      dataIndex: "sessionId",
      key: "sessionId",
    },
    {
      title: "Session Date",
      dataIndex: "date",
      key: "date",
      render: (colData: any, row: any) => colData,
    },
    {
      title: "Time slot",
      dataIndex: "time",
      key: "time",
      render: (colData: any, row: any) => (
        <Box display="flex" justifyContent="center">
          {getSessionTimeslots(row).map((item: any, idx: number) => (
            <Typography key={idx} sx={{ fontSize: "13px" }}>
              {item?.timeslot?.startAt} - {item?.timeslot?.endAt}
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      title: "Speciality",
      dataIndex: "clinician.designation",
      key: "clinician.designation",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (colData: any) => <StatusBadge status={colData} />,
    },
  ];

  const column2 = [
    {
      title: "Session ID",
      dataIndex: "sessionId",
      key: "sessionId",
    },
    {
      title: "Client Name",
      dataIndex: "patientProfile.fullName",
      key: "patientProfile.fullName",
    },
    {
      title: "Session Date",
      dataIndex: "date",
      key: "date",
      render: (colData: any, row: any) => colData,
    },
    {
      title: "Time slot",
      dataIndex: "time",
      key: "time",
      render: (colData: any, row: any) => (
        <Box display="flex" justifyContent="center">
          {getSessionTimeslots(row).map((item: any, idx: number) => (
            <Typography key={idx} sx={{ fontSize: "13px" }}>
              {item?.timeslot?.startAt} - {item?.timeslot?.endAt}
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (colData: any) => <StatusBadge status={colData || "COMPLETED"} />,
    },
  ];

  const clinicianColumn = [
    {
      title: "Name",
      dataIndex: "user.fullName",
      key: "user.fullName",
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      render: (colData: any, row: any) => textCapitalization(colData || ""),
    },
    {
      title: "Speciality",
      dataIndex: "designation",
      key: "designation",
    },
    {
      title: "Total Sessions",
      dataIndex: "totalSessions",
      key: "totalSessions",
    },
  ];

  const patientColumn = [
    {
      title: "Name",
      dataIndex: "fullName",
      key: "fullName",
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      render: (colData: any, row: any) => textCapitalization(colData),
    },
    {
      title: "Total Sessions",
      dataIndex: "totalSessions",
      key: "totalSessions",
    },
    {
      title: "Amount Spent",
      dataIndex: "totalSessionAmount",
      key: "totalSessionAmount",
      render: (colData: any) => (
        <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
          {Number(colData).toLocaleString()} SAR
        </Typography>
      ),
    },
  ];

  const barData = analyticalData?.data?.session?.statuses?.map((item: any) => ({
    name: textCapitalization(item?.title),
    count: +item?.count,
  }));

  const barNamesAndColors = [
    { label: "Number of Sessions", color: "#D58E4E" },
  ];

  return (
    <Box mb={4} sx={{ animation: "fadeIn 0.4s ease" }}>
      {/* ──── Page Header ──── */}
      <Box mb={3.5}>
        <Typography
          sx={{
            fontSize: "26px",
            fontWeight: 800,
            color: "#0F172A",
            letterSpacing: "-0.03em",
            mb: 0.5,
          }}
        >
          Dashboard
        </Typography>
        <Typography sx={{ fontSize: "14px", color: "#64748B" }}>
          Overview of your platform performance and key metrics
        </Typography>
      </Box>

      {/* ──── Key Metrics ──── */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} md={4}>
          <StatsCard
            icon={<PeopleAltRoundedIcon sx={{ fontSize: 20 }} />}
            label="Total Users"
            value={analyticalData?.data?.user?.users || 0}
            color="#0EA5E9"
            subtitle="Active platform users"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatsCard
            icon={<LocalHospitalRoundedIcon sx={{ fontSize: 20 }} />}
            label="Total Clinicians"
            value={analyticalData?.data?.clinician?.clinicians || 0}
            color="#10B981"
            subtitle="Registered clinicians"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatsCard
            icon={<EventNoteRoundedIcon sx={{ fontSize: 20 }} />}
            label="Total Sessions"
            value={analyticalData?.data?.session?.total || 0}
            color="#D58E4E"
            subtitle="All-time sessions"
          />
        </Grid>
      </Grid>

      {/* ──── Charts Row ──── */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} md={6}>
          <Card sx={premiumCard}>
            <Box sx={sectionHeader}>
              <Box sx={sectionIconBox("#0EA5E9")}>
                <PeopleAltRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={sectionTitle}>Users by Gender</Typography>
            </Box>
            <Box p={2.5} width="100%" height="260px" display="flex">
              {analyticalData?.data?.user?.gender?.every(
                (item: any) => item.count === 0
              ) ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="100%"
                >
                  <Typography
                    sx={{ fontSize: "14px", color: "#94A3B8", fontWeight: 500 }}
                  >
                    No data available yet
                  </Typography>
                </Box>
              ) : (
                <>
                  <ResponsiveContainer width="60%" height="100%">
                    <PieChart>
                      <Pie
                        data={analyticalData?.data?.user?.gender}
                        dataKey="count"
                        nameKey="title"
                        cx="45%"
                        cy="50%"
                        innerRadius={65}
                        outerRadius={90}
                        strokeWidth={3}
                        stroke="#FFFFFF"
                      >
                        {analyticalData?.data?.user?.gender.map(
                          (entry: any, index: number) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          )
                        )}
                      </Pie>
                      <Tooltip content={<CustomTooltipContent />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    justifyContent="center"
                  >
                    <LegendDot color="#0EA5E9" label="Male" />
                    <LegendDot color="#D58E4E" label="Female" />
                    <LegendDot color="#10B981" label="Others" />
                  </Box>
                </>
              )}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={premiumCard}>
            <Box sx={sectionHeader}>
              <Box sx={sectionIconBox("#8B5CF6")}>
                <InsightsRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={sectionTitle}>Consultation Type</Typography>
            </Box>
            <Box p={2.5} width="100%" height="260px" display="flex">
              <ResponsiveContainer width="60%" height="100%">
                <PieChart>
                  <Pie
                    data={analyticalData?.data?.session?.modes}
                    dataKey="count"
                    nameKey="title"
                    cx="45%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={90}
                    strokeWidth={3}
                    stroke="#FFFFFF"
                  >
                    {analyticalData?.data?.session?.modes?.map(
                      (entry: any, index: number) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      )
                    )}
                  </Pie>
                  <Tooltip content={<CustomTooltipContent />} />
                </PieChart>
              </ResponsiveContainer>
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                justifyContent="center"
              >
                <LegendDot color="#0EA5E9" label="Audio" />
                <LegendDot color="#D58E4E" label="Video" />
                <LegendDot color="#10B981" label="At Home" />
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* ──── Session Analytics Bar Chart ──── */}
      <Card sx={{ ...premiumCard, mb: 3.5 }}>
        <Box sx={sectionHeader}>
          <Box sx={sectionIconBox("#D58E4E")}>
            <TrendingUpRoundedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={sectionTitle}>Session Analytics</Typography>
        </Box>
        <BarChartCard
          layout="vertical"
          data={barData}
          barNamesAndColors={barNamesAndColors}
          dataSource={analyticalData?.data?.session?.statuses}
        />
      </Card>

      {/* ──── Upcoming Sessions Table ──── */}
      <Card sx={{ ...premiumCard, mb: 3.5 }}>
        <Box sx={sectionHeader}>
          <Box sx={sectionIconBox("#0EA5E9")}>
            <ScheduleRoundedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={sectionTitle}>Upcoming Sessions</Typography>
          <Chip
            label={`${sessionData?.data?.count || 0} total`}
            size="small"
            sx={{
              ml: "auto",
              fontSize: "11px",
              fontWeight: 600,
              height: "24px",
              borderRadius: "6px",
              backgroundColor: alpha("#0EA5E9", 0.08),
              color: "#0EA5E9",
            }}
          />
        </Box>
        <Table
          headAlign={"center"}
          tableAlign={"center"}
          key={"sessions"}
          dataSource={sessionData?.data?.count === 0 ? null : topSessionData}
          rowKey={"id"}
          columns={column}
        />
      </Card>

      {/* ──── Pending Reports Table ──── */}
      <Card sx={{ ...premiumCard, mb: 3.5 }}>
        <Box sx={sectionHeader}>
          <Box sx={sectionIconBox("#EA580C")}>
            <AssignmentRoundedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={sectionTitle}>Pending Reports</Typography>
          <Chip
            label={`${reportData?.data?.count || 0} total`}
            size="small"
            sx={{
              ml: "auto",
              fontSize: "11px",
              fontWeight: 600,
              height: "24px",
              borderRadius: "6px",
              backgroundColor: alpha("#EA580C", 0.08),
              color: "#EA580C",
            }}
          />
        </Box>
        <Table
          headAlign={"center"}
          tableAlign={"center"}
          key={"sessions-report"}
          dataSource={reportData?.data?.count === 0 ? null : topReportData}
          rowKey={"id"}
          columns={column2}
        />
      </Card>

      {/* ──── Top 5 Rankings ──── */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Card sx={premiumCard}>
            <Box sx={sectionHeader}>
              <Box sx={sectionIconBox("#10B981")}>
                <StarRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={sectionTitle}>Top 5 Clinicians</Typography>
            </Box>
            <Table
              headAlign={"center"}
              tableAlign={"center"}
              key={"top-clinicians"}
              dataSource={
                top5Data?.data?.clinicians?.length === 0
                  ? null
                  : top5Data?.data?.clinicians
              }
              rowKey={"id"}
              columns={clinicianColumn}
            />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={premiumCard}>
            <Box sx={sectionHeader}>
              <Box sx={sectionIconBox("#8B5CF6")}>
                <StarRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={sectionTitle}>Top 5 Patients</Typography>
            </Box>
            <Table
              headAlign={"center"}
              tableAlign={"center"}
              key={"top-patients"}
              dataSource={
                top5Data?.data?.patientsProfiles?.length === 0
                  ? null
                  : top5Data?.data?.patientsProfiles
              }
              rowKey={"id"}
              columns={patientColumn}
            />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
